import {
  Stack,
  ContentRow,
  Box,
  useIsMobileLayout,
  Body,
  Card,
  Space,
  Divider,
  Button,
  LoadingButton,
  OnlineIcon,
  UploadIcon,
  Dialog,
  RadioGroup,
  LocalizedString,
  EmailIcon,
  MobileIcon,
  Banner,
  UnorderedList,
  Loader,
  SpinnerIcon,
  FirstIcon,
} from "@dbn/omnichannel-design-system-g4p";
import { palette } from "@dbn/omnichannel-design-system-g4p/lib/styleConstants";
import { array, option, taskEither } from "fp-ts";
import { IO } from "fp-ts/IO";
import { LocaleKey, useFormatMessage } from "../../intl";
import { BackButton } from "../../Common/BackButton/BackButton";
import { PageHeading } from "../../Common/PageHeading/PageHeading";
import { FilePDFDialog } from "../../Common/Dialogs/FilePDFDialog/FilePDFDialog";
import { constFalse, flow, identity, pipe } from "fp-ts/function";
import { useEffect, useState } from "react";
import { useCommand, usePollingEffect } from "../../useAPI";
import * as documentAPI from "../../Common/documentAPI";
import { NonEmptyArray } from "fp-ts/NonEmptyArray";
import { Docset } from "../../Common/documentAPI";
import * as api from "./api";
import * as loanApi from "../api";
import { useIsRemoteChannel } from "../../useChannel";
import { FeedbackMessage } from "../../Common/FeedbackMessage/FeedbackMessage";
import { useAppContext } from "../../useAppContext";

type State = "loadingDocs" | "loadingFlags" | "cards" | "share" | "loading";
type ShareType = "sms" | "email";

type Props = {
  onBack: IO<unknown>;
  onComplete: () => unknown;
};

export function ProofOfIncomeTypeSelection(props: Props) {
  const formatMessage = useFormatMessage();
  const isMobileLayout = useIsMobileLayout();
  const isRemote = useIsRemoteChannel();
  const [state, setState] = useState<State>("loadingDocs");
  const [moreInfo, setMoreInfo] = useState(false);
  const [gdprRequired, setGdprRequired] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const docTemplates = useCommand(documentAPI.templates);
  const docContent = useCommand(documentAPI.content);
  const fetchNeosFlag = useCommand(api.getNeosFlag);
  const fetchNeosStatus = useCommand(api.getNeosStatus);
  const fetchGdprRequired = useCommand(api.getGdprRequired);
  const acceptGdpr = useCommand(api.saveGdprConsent);
  const getUserEmail = useCommand(loanApi.userEmail);
  const getUserPhone = useCommand(loanApi.phoneNumber);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [shareType, setShareType] = useState<ShareType>("sms");
  const [shareDialog, setShareDialog] = useState(false);
  const fetchLink = useCommand(api.getLink);
  const shareLink = useCommand(api.shareLink);
  const [remainingResend, setRemainingResend] = useState<number | undefined>();
  const { config } = useAppContext();
  const [requiredDocumentsList, setRequiredDocumentsList] = useState<
    LocalizedString[]
  >([]);
  const requiredDocuments = useCommand(loanApi.previewRequiredDocuments);

  const docsetsMoreInfo: NonEmptyArray<Docset> = ["NeosIncomeVerification"];
  const docsetsGdpr: NonEmptyArray<Docset> = ["NeosGDPRIncomeVerification"];

  useEffect(() => {
    if (!config.enableNeos) {
      props.onComplete();
    }
  }, []);

  useEffect(() => {
    pipe(
      fetchNeosStatus(),
      taskEither.fold(
        _ => taskEither.fromIO(() => {}),
        neos =>
          taskEither.fromIO(() => {
            if (neos.ok) {
              props.onComplete();
            }
          })
      )
    )();
  }, []);

  usePollingEffect(loanApi.checkRequiredDocuments, {
    intervalMS: 1000,
    shouldPollingContinue: ({ requiredDocumentsReceived }) =>
      !requiredDocumentsReceived,
    onSuccess: ({ requiredDocumentsReceived }) =>
      requiredDocumentsReceived &&
      pipe(
        requiredDocuments(),
        taskEither.bimap(
          () => {},
          list => {
            setState("loadingFlags");
            const documentList: LocalizedString[] = list
              .filter(document => document.section === "MainIncome")
              .map(document => document.documentName);
            setRequiredDocumentsList(documentList);
          }
        )
      )(),
    onError: () => {},
  });

  useEffect(() => {
    if (state !== "loadingFlags") {
      return;
    }
    pipe(
      fetchNeosFlag(),
      taskEither.fold(
        _ => taskEither.fromIO(() => {}),
        neos =>
          taskEither.fromIO(() => {
            if (neos.flag) {
              setState("cards");
            } else {
              props.onComplete();
            }
          })
      )
    )();
    pipe(
      fetchGdprRequired(),
      taskEither.fold(
        _ => taskEither.fromIO(() => {}),
        gdpr => taskEither.fromIO(() => setGdprRequired(gdpr))
      )
    )();
  }, [state]);

  const onContinueStandard = () => {
    props.onComplete();
  };

  const onContinueNeos = () => {
    setState("loading");
    if (!isRemote) {
      setState("share");
    } else {
      pipe(
        fetchLink(),
        taskEither.fold(
          _ => taskEither.fromIO(() => {}),
          ({ link }) => taskEither.fromIO(() => window.location.replace(link))
        )
      )();
    }
  };

  const onContinueNeosWithGdprCheck = () => {
    setMoreInfo(false);
    if (gdprRequired) {
      setGdpr(true);
    } else {
      onContinueNeos();
    }
  };

  const onAcceptGdpr = () => {
    pipe(
      acceptGdpr(),
      taskEither.fold(
        _ => taskEither.fromIO(() => {}),
        _ =>
          taskEither.fromIO(() => {
            setGdprRequired(false);
            onContinueNeos();
            setGdpr(false);
          })
      )
    )();
  };

  const onShareLink = pipe(
    shareLink({ channel: shareType.toUpperCase() }),
    taskEither.fold(
      _ => taskEither.fromIO(() => {}),
      ({ remainingAttempts }) =>
        taskEither.fromIO(() => {
          setRemainingResend(remainingAttempts);
        })
    )
  );

  const onContinueShareWithShareType =
    shareType === "sms"
      ? pipe(
          taskEither.fromIO(() => onShareLink()),
          taskEither.chain(() => getUserPhone()),
          taskEither.fold(
            _ => taskEither.fromIO(() => {}),
            res =>
              taskEither.fromIO(() => {
                setPhoneNumber(res.phoneNumber);
                setShareDialog(true);
              })
          )
        )
      : pipe(
          shareLink({ channel: "EMAIL" }),
          taskEither.chain(() => getUserEmail()),
          taskEither.fold(
            _ => taskEither.fromIO(() => {}),
            res =>
              taskEither.fromIO(() => {
                setEmail(res.email);
                setShareDialog(true);
              })
          )
        );

  const Cards = () => {
    return (
      <Box
        style={{
          display: "flex",
          alignItems: "center",
        }}
        column={isMobileLayout}
        shrink={!isMobileLayout}
      >
        <Box
          style={
            isMobileLayout
              ? { flex: "1 0 auto", width: "100%" }
              : { flex: "0 1 auto", width: "100%", height: "100%" }
          }
        >
          <Card grow>
            <Stack
              width={"100%"}
              units={4}
              marginTop={isMobileLayout ? undefined : "20px"}
            >
              <Box>
                <OnlineIcon
                  size={"x-large"}
                  monochromatic
                  color={palette.dark}
                />
              </Box>
              <Stack width={"100%"} units={4} column shrink>
                <Stack
                  units={4}
                  column
                  height={isMobileLayout ? "unset" : "120px"}
                  wrap
                >
                  <Body size="medium" weight="bold" overflowWrap={"break-word"}>
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeOnline.Title"
                    )}
                  </Body>
                  <Body size="small" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeOnline.Body"
                    )}
                  </Body>
                </Stack>
                <Stack
                  vAlignContent={"center"}
                  hAlignContent={"center"}
                  column={isMobileLayout}
                >
                  <Body size="medium" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeOnline.Step1"
                    )}
                  </Body>
                  <Space units={2} />
                  <div
                    style={{
                      transform: isMobileLayout ? "rotate(90deg)" : undefined,
                      display: "inline-blocl",
                    }}
                  >
                    <FirstIcon
                      size={"medium"}
                      color={palette.dark}
                      monochromatic={true}
                    />
                  </div>
                  <Space units={4} />
                  <Body size="medium" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeOnline.Step2"
                    )}
                  </Body>
                  <Space units={2} />
                  <div
                    style={{
                      transform: isMobileLayout ? "rotate(90deg)" : undefined,
                      display: "inline-blocl",
                    }}
                  >
                    <FirstIcon
                      size={"medium"}
                      color={palette.dark}
                      monochromatic={true}
                    />
                  </div>
                  <Space units={4} />
                  <Body size="medium" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeOnline.Step3"
                    )}
                  </Body>
                </Stack>
                <Box
                  column={isMobileLayout}
                  vAlignContent={"center"}
                  style={
                    isMobileLayout
                      ? { width: "100%" }
                      : { justifyContent: "space-between", alignItems: "end" }
                  }
                  grow
                >
                  <Button
                    type="button"
                    label={formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.MoreInfo.ButtonLabel"
                    )}
                    variant="text"
                    size="small"
                    action={() => {
                      setMoreInfo(true);
                    }}
                  />
                  <Space units={2} />
                  <LoadingButton
                    type="submit"
                    variant="primary"
                    size="small"
                    action={taskEither.fromIO(() =>
                      onContinueNeosWithGdprCheck()
                    )}
                    labels={{
                      normal: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                      success: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                      loading: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                      error: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Card>
        </Box>

        <Space units={4} />

        <Box
          style={
            isMobileLayout
              ? { flex: "1 0 auto" }
              : { flex: "0 1 auto", width: "100%", height: "100%" }
          }
        >
          <Card grow>
            <Stack
              width={"100%"}
              units={4}
              marginTop={isMobileLayout ? undefined : "20px"}
            >
              <Box>
                <UploadIcon size={"x-large"} color={palette.dark} />
              </Box>
              <Stack width={"100%"} units={4} column shrink>
                <Stack
                  units={4}
                  column
                  height={isMobileLayout ? "unset" : "120px"}
                  wrap
                >
                  <Body size="medium" weight="bold">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeStandard.Title"
                    )}
                  </Body>
                  <Body size="small" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeStandard.Body"
                    )}
                  </Body>
                </Stack>
                <Stack
                  vAlignContent={"center"}
                  hAlignContent={"center"}
                  column={isMobileLayout}
                >
                  <Body size="medium" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeStandard.Step1"
                    )}
                  </Body>
                  <Space units={2} />
                  <div
                    style={{
                      transform: isMobileLayout ? "rotate(90deg)" : undefined,
                      display: "inline-blocl",
                    }}
                  >
                    <FirstIcon
                      size={"medium"}
                      color={palette.dark}
                      monochromatic={true}
                    />
                  </div>
                  <Space units={4} />
                  <Body size="medium" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeStandard.Step2"
                    )}
                  </Body>
                  <Space units={2} />
                  <div
                    style={{
                      transform: isMobileLayout ? "rotate(90deg)" : undefined,
                      display: "inline-blocl",
                    }}
                  >
                    <FirstIcon
                      size={"medium"}
                      color={palette.dark}
                      monochromatic={true}
                    />
                  </div>
                  <Space units={4} />
                  <Body size="medium" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.TypeStandard.Step3"
                    )}
                  </Body>
                </Stack>
                <Box
                  column
                  style={isMobileLayout ? { width: "100%" } : undefined}
                  {...(!isMobileLayout && { hAlignContent: "right" })}
                >
                  <LoadingButton
                    type="submit"
                    variant="primary"
                    size="small"
                    action={taskEither.fromIO(() => onContinueStandard())}
                    labels={{
                      normal: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                      success: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                      loading: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                      error: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Choose.ButtonLabel"
                      ),
                    }}
                  />
                </Box>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>
    );
  };

  const CardWrapper = (props: { children: React.ReactElement<unknown> }) => {
    return isMobileLayout ? (
      <Box column shrink grow>
        {props.children}
      </Box>
    ) : (
      <Box column shrink>
        <Card column>{props.children}</Card>
      </Box>
    );
  };

  switch (state) {
    case "loading":
      return (
        <Box hAlignContent="center" vAlignContent="center" column>
          <SpinnerIcon size="xx-large" />
        </Box>
      );
    case "cards":
      return (
        <Stack units={10} column shrink>
          <PageHeading
            title={formatMessage(
              "StandardLoan.ProofOfIncomeTypeSelection.Title"
            )}
            contentRowType="full"
          />
          <CardWrapper>
            <>
              <Box width="100%" column>
                <Body size="large" weight="bold">
                  {formatMessage(
                    "StandardLoan.ProofOfIncomeTypeSelection.CardSecondTitle"
                  )}
                </Body>
                {array.isNonEmpty(requiredDocumentsList) && (
                  <UnorderedList size="large" listStyle="bullet">
                    {requiredDocumentsList}
                  </UnorderedList>
                )}
                <Space units={8} />
                <Divider />
                <Space units={4} />
                <Box column shrink grow>
                  <Body size="large" weight="bold">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.CardTitle"
                    )}
                  </Body>
                </Box>
                <Box style={{ paddingTop: "6px", paddingBottom: "6px" }}>
                  <Body size="small" weight="regular">
                    {formatMessage(
                      "StandardLoan.ProofOfIncomeTypeSelection.CardBody"
                    )}
                  </Body>
                </Box>
              </Box>

              <Space units={8} />
              <Divider />

              <Space units={8} />
              <Cards />
            </>
          </CardWrapper>

          <ContentRow type="full">
            <Box
              column
              shrink
              grow
              hAlignContent={isMobileLayout ? "center" : "left"}
            >
              <BackButton action={props.onBack} />
            </Box>
          </ContentRow>
          {moreInfo && (
            <FilePDFDialog
              key="moreInfo"
              onDismiss={() => setMoreInfo(false)}
              title={formatMessage(
                "StandardLoan.ProofOfIncomeTypeSelection.MoreInfoDialog.Title"
              )}
              file={pipe(
                docTemplates({ docsets: docsetsMoreInfo }),
                taskEither.chain(items => docContent({ docId: items[0].docId }))
              )}
              onConfirm={{
                label: formatMessage(
                  "StandardLoan.ProofOfIncomeTypeSelection.MoreInfoDialog.Continue.ButtonLabel"
                ),
                action: () => onContinueNeosWithGdprCheck(),
              }}
            />
          )}
          {gdpr && (
            <FilePDFDialog
              key="gdpr"
              onDismiss={() => setGdpr(false)}
              title={formatMessage(
                "StandardLoan.ProofOfIncomeTypeSelection.GdprDialog.Title"
              )}
              file={pipe(
                docTemplates({ docsets: docsetsGdpr }),
                taskEither.chain(items => docContent({ docId: items[0].docId }))
              )}
              onConfirm={{
                label: formatMessage(
                  "StandardLoan.ProofOfIncomeTypeSelection.GdprDialog.Continue.ButtonLabel"
                ),
                checkboxLabel: formatMessage(
                  "StandardLoan.ProofOfIncomeTypeSelection.GdprDialog.Checkbox"
                ),
                action: () => onAcceptGdpr(),
              }}
            />
          )}
        </Stack>
      );
    case "share":
      return (
        <Stack units={10} column shrink>
          <PageHeading
            title={formatMessage(
              "StandardLoan.ProofOfIncomeTypeSelection.Share.Title"
            )}
            contentRowType="full"
          />
          <CardWrapper>
            <Box width="100%" column marginTop={12} marginBottom={12}>
              <Box>
                <RadioGroup
                  name="shareType"
                  variant="vertical"
                  value={option.some(shareType)}
                  onChange={option.map(s => setShareType(s))}
                  options={["sms" as ShareType, "email" as ShareType]}
                  optionKey={identity}
                  isOptionDisabled={constFalse}
                  renderOption={(_: ShareType) => "" as LocalizedString}
                  renderOptionChildren={flow(
                    (stype: ShareType) => (
                      <Box>
                        {stype === "sms" ? (
                          <MobileIcon
                            size="x-large"
                            color={palette.dark}
                            monochromatic
                          />
                        ) : (
                          <EmailIcon
                            size="x-large"
                            color={palette.dark}
                            monochromatic
                          />
                        )}
                        <Box width="100%" column>
                          <Body size="large" weight="bold">
                            {formatMessage(
                              `StandardLoan.ProofOfIncomeTypeSelection.Share.${stype}.Title` as LocaleKey
                            )}
                          </Body>
                          <Body size="medium" weight="regular">
                            {formatMessage(
                              `StandardLoan.ProofOfIncomeTypeSelection.Share.${stype}.Body` as LocaleKey
                            )}
                          </Body>
                        </Box>
                      </Box>
                    ),
                    option.some
                  )}
                  issuesType={option.none}
                />
              </Box>
            </Box>
          </CardWrapper>

          <ContentRow type="full">
            <Box
              column={isMobileLayout}
              shrink
              grow
              hAlignContent={isMobileLayout ? "center" : "left"}
              style={{
                justifyContent: "space-between",
              }}
            >
              <BackButton action={() => setState("cards")} />
              <Button
                variant="primary"
                size="default"
                label={formatMessage(
                  "StandardLoan.ProofOfIncomeTypeSelection.Share.Continue.ButtonLabel"
                )}
                action={onContinueShareWithShareType}
              />
            </Box>
          </ContentRow>
          {shareDialog && (
            <Dialog
              onDismiss={option.some(() => setState("cards"))}
              variant="left"
              size={"medium"}
              title={"" as LocalizedString}
              actions={[
                {
                  variant: "primary",
                  action: taskEither.fromIO(props.onComplete),
                  label: formatMessage(
                    "StandardLoan.ProofOfIncomeTypeSelection.Share.OK.ButtonLabel"
                  ),
                },
              ]}
            >
              <Stack column units={6}>
                <FeedbackMessage
                  Icon={shareType === "sms" ? MobileIcon : EmailIcon}
                  heading={formatMessage(
                    `StandardLoan.ProofOfIncomeTypeSelection.Share.${shareType}.Dialog.SubTitle` as LocaleKey
                  )}
                  subHeading={formatMessage(
                    `StandardLoan.ProofOfIncomeTypeSelection.Share.${shareType}.Dialog.Body` as LocaleKey,
                    { phoneNumber, email }
                  )}
                  backgroundColor={palette.blue800}
                />
                <Box vAlignContent="center" hAlignContent="center">
                  <Banner
                    type={remainingResend === 0 ? "warning" : "informative"}
                    content={formatMessage(
                      remainingResend === 1
                        ? "StandardLoan.ProofOfIncomeTypeSelection.Share.Dialog.Banner.OneAttemptLeft"
                        : remainingResend === 0
                        ? "StandardLoan.ProofOfIncomeTypeSelection.Share.Dialog.Banner.NoAttemptLeft"
                        : "StandardLoan.ProofOfIncomeTypeSelection.Share.Dialog.Banner"
                    )}
                    title={option.none}
                    actions={option.none}
                    onDismiss={option.none}
                  />
                </Box>
                <Box vAlignContent="center" hAlignContent="center">
                  <Body size="medium" weight="medium">
                    {formatMessage(
                      `StandardLoan.ProofOfIncomeTypeSelection.Share.${shareType}.Dialog.Button.Message` as LocaleKey
                    )}
                  </Body>
                  <Space units={2} />
                  <LoadingButton
                    disabled={remainingResend === 0}
                    size="default"
                    labels={{
                      normal: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Share.Retry.ButtonLabel"
                      ),
                      success: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Share.Retry.ButtonLabel"
                      ),
                      loading: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Share.Retry.ButtonLabel"
                      ),
                      error: formatMessage(
                        "StandardLoan.ProofOfIncomeTypeSelection.Share.Retry.ButtonLabel"
                      ),
                    }}
                    variant="text"
                    action={onShareLink}
                  />
                </Box>
              </Stack>
            </Dialog>
          )}
        </Stack>
      );
    case "loadingDocs":
    case "loadingFlags":
    default:
      return (
        <Stack column units={8} grow shrink>
          <Box hAlignContent="center">
            <Loader />
          </Box>
          <Box column>
            <Body size="medium" align="center" weight="regular">
              {formatMessage(
                "StandardLoan.OfferApproved.OfferReview.documentationLoading"
              )}
            </Body>
          </Box>
        </Stack>
      );
  }
}
